import { useCallback, useEffect, useMemo, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Form, Field, TextInput, InputStatuses } from '@carboledger/ui';
import { useConvertUnitsQuery } from '@/lib/api/unitsApi';
import { resolveQuantityColumns } from '@/lib/mappers';
import { useDebounce, useTranslations } from '@/hooks';
import { shipmentValidation } from '../incomingShipmentsValidation';
import { densityRequiredRule } from '@/lib/validation';
import {
  normalizeUnit,
  needsDensity,
  getSaveableTrackingUnitOptions,
  getDensityUnits,
  EMPTY_LIST,
  REMOVED_LINKS,
  removedLinksOf,
  withRemovedLink,
} from '@/lib/utils';
import {
  LinkedEntityField,
  TrackingUnitField,
  ShipmentQuantityField,
  ShipmentQuantityUnitField,
} from '@/components/common';

// Derived quantities are display-only, so trim the float noise conversion introduces.
const formatDerived = (value) => (value == null ? '' : String(Number(value.toFixed(4))));

/**
 * Props:
 *   materials  material view models, the source of the declaration/working units
 *   material   overrides that lookup where the form has no Material dropdown to read —
 *              Receive shipment, whose contract already resolved the units and the density
 *   derivedFrom  { quantity, unit } the read-only columns convert instead of the entry —
 *              Receive shows the CONTRACT's amount there while the entry is the user's
 *   values     current form values
 *   setValue   (name, value) => void
 *   linkedContracts   the shipment's linked contracts, shown as chips
 */
export const ShipmentQuantityFields = ({
  materials,
  material: materialOverride,
  derivedFrom,
  values,
  setValue,
  linkedContracts,
}) => {
  const t = useTranslations();

  const material = useMemo(
    () =>
      materialOverride ?? (materials ?? []).find((entry) => entry.id === String(values.materialId)),
    [materialOverride, materials, values.materialId],
  );

  // Removing a chip only marks it; the Edit page unlinks after the save succeeds.
  const handleUnlinkContract = useCallback(
    (contractDbId) => setValue(REMOVED_LINKS, withRemovedLink(values, contractDbId)),
    [setValue, values],
  );

  const declarationUnit = normalizeUnit(material?.declarationUnit);
  const workingUnit = normalizeUnit(material?.workingUnit);
  const trackingUnit = normalizeUnit(values.trackingUnit);

  // No working unit configured → the whole middle row is hidden (doc "Part 2").
  const hasWorkingUnit = Boolean(workingUnit);
  const workingDensityRequired = hasWorkingUnit && needsDensity(declarationUnit, workingUnit);
  const trackingDensityRequired =
    Boolean(trackingUnit) && needsDensity(declarationUnit, trackingUnit);

  // Receive converts the contract's amount, so it is fixed and needs no debounce.
  const sourceQuantity = derivedFrom ? derivedFrom.quantity : values.shipmentQuantity;
  const quantity = useDebounce(sourceQuantity);
  const workingDensity = useDebounce(values.workingDensity);
  const trackingDensity = useDebounce(values.trackingDensity);

  // Receive splits these: the columns convert the contract's amount, the payload the user's.
  const convertedUnit =
    normalizeUnit(derivedFrom?.unit ?? values.shipmentQuantityUnit) || declarationUnit;
  const savedUnit = normalizeUnit(values.shipmentQuantityUnit) || declarationUnit;

  const conversionNeedsWorkingDensity = workingDensityRequired && convertedUnit === workingUnit;
  const conversionNeedsTrackingDensity = trackingDensityRequired && convertedUnit === trackingUnit;

  // Both units are always offered; saving in the working one is what demands the density.
  const workingDensityMandatory = workingDensityRequired && savedUnit === workingUnit;
  const missingWorkingDensity = workingDensityMandatory && !values.workingDensity;

  // Normalised, so the dropdown, the mirrored column and the payload agree on which unit is which.
  const conversionMaterial = useMemo(
    () => (material ? { declarationUnit, workingUnit } : null),
    [material, declarationUnit, workingUnit],
  );

  // One request, all three units; only the ENTERED leg's factor is worth waiting for.
  const conversionArgs = useMemo(() => {
    if (!conversionMaterial || !quantity) return skipToken;
    if (!hasWorkingUnit && !trackingUnit) return skipToken;
    if (conversionNeedsWorkingDensity && !workingDensity) return skipToken;
    if (conversionNeedsTrackingDensity && !trackingDensity) return skipToken;
    return {
      material: conversionMaterial,
      unit: convertedUnit,
      quantity,
      density: workingDensity,
      trackingUnit,
      trackingDensity,
    };
  }, [
    conversionMaterial,
    hasWorkingUnit,
    quantity,
    conversionNeedsWorkingDensity,
    workingDensity,
    trackingUnit,
    conversionNeedsTrackingDensity,
    trackingDensity,
    convertedUnit,
  ]);

  const { data: conversion, error: conversionError } = useConvertUnitsQuery(conversionArgs);

  // Entering in the working unit with no density leaves nothing to convert back with.
  const workingDensityError = missingWorkingDensity
    ? t('SHIPMENT_FORM_DENSITY_REQUIRED_FOR_ENTRY')
    : conversionError
      ? t('SHIPMENT_FORM_CONVERSION_FAILED')
      : null;

  const shipmentAmount = Number.parseFloat(sourceQuantity);
  const { declarationQuantity, workingQuantity, trackingQuantity } = useMemo(
    () =>
      resolveQuantityColumns({
        entered: Number.isFinite(shipmentAmount) ? shipmentAmount : null,
        unit: convertedUnit,
        material: conversionMaterial,
        trackingUnit,
        conversion,
      }),
    [shipmentAmount, convertedUnit, conversionMaterial, trackingUnit, conversion],
  );

  const trackingUnitOptions = useMemo(() => {
    const units = getSaveableTrackingUnitOptions(declarationUnit, workingUnit);
    // A blank first entry lets the user clear a tracking unit they picked by mistake.
    return [
      { value: '', label: t('SHIPMENT_FORM_TRACKING_UNIT_NONE') },
      ...units.map((unit) => ({
        value: unit,
        label: unit,
        badgeText: t('UNIT_TRACKING'),
      })),
    ];
  }, [declarationUnit, workingUnit, t]);

  // The material's own density owns the field whenever it has one — as in <ContractForm>.
  const densityFromMaterial = Boolean(material?.density) && workingDensityRequired;
  const previousMaterialId = useRef(values.materialId);
  useEffect(() => {
    if (previousMaterialId.current === values.materialId) return;
    previousMaterialId.current = values.materialId;
    setValue('workingDensity', '');
  }, [values.materialId, setValue]);

  useEffect(() => {
    if (!densityFromMaterial) return;
    const materialDensity = String(material.density);
    if (values.workingDensity !== materialDensity) setValue('workingDensity', materialDensity);
  }, [densityFromMaterial, material, values.workingDensity, setValue]);

  // A tracking density with no tracking unit is exactly what the server rejects.
  useEffect(() => {
    if (!trackingUnit && values.trackingDensity) setValue('trackingDensity', '');
  }, [trackingUnit, values.trackingDensity, setValue]);

  // The density that was typed belongs to the old pair, so it goes with the unit.
  const handleTrackingUnitChange = useCallback(
    (event) => {
      setValue('trackingUnit', event.target.value);
      setValue('trackingDensity', '');
    },
    [setValue],
  );

  return (
    <Form.Section title={t('SECTION_QUANTITY')}>
      <Form.Row columns={4}>
        <TrackingUnitField
          options={trackingUnitOptions}
          onChange={handleTrackingUnitChange}
          rules={shipmentValidation.trackingUnit}
        />

        {trackingUnit ? (
          <FactorOrDensityField
            name="trackingDensity"
            from={declarationUnit}
            to={trackingUnit}
            isDensity={trackingDensityRequired}
            isTracking
            factor={conversion?.trackingFactor ?? null}
            errorMessage={conversionError ? t('SHIPMENT_FORM_CONVERSION_FAILED') : null}
            rules={
              trackingDensityRequired
                ? shipmentValidation.trackingDensityRequired
                : shipmentValidation.density
            }
          />
        ) : null}

        {trackingUnit ? (
          <DerivedQuantity
            label={t('SHIPMENT_FORM_SHIPMENT_QUANTITY_TRACKING', { unit: trackingUnit })}
            value={formatDerived(trackingQuantity)}
          />
        ) : null}
      </Form.Row>

      <Form.Row columns={4}>
        <ShipmentQuantityField rules={shipmentValidation.shipmentQuantity} />

        <QuantityUnitField declarationUnit={declarationUnit} workingUnit={workingUnit} />

        <LinkedContracts
          linkedContracts={linkedContracts}
          removed={removedLinksOf(values)}
          onUnlink={handleUnlinkContract}
        />
      </Form.Row>

      <Form.Row columns={4}>
        <DerivedQuantity
          label={t('SHIPMENT_FORM_SHIPMENT_QUANTITY_DECLARATION', {
            unit: declarationUnit || t('SHIPMENT_FORM_DECLARATION_UNIT'),
          })}
          value={formatDerived(declarationQuantity)}
        />

        {hasWorkingUnit ? (
          <FactorOrDensityField
            name="workingDensity"
            from={declarationUnit}
            to={workingUnit}
            isDensity={workingDensityRequired}
            factor={conversion?.workingFactor ?? null}
            disabled={densityFromMaterial}
            errorMessage={workingDensityError}
            rules={workingDensityMandatory ? densityRequiredRule : shipmentValidation.density}
          />
        ) : null}

        {hasWorkingUnit ? (
          <DerivedQuantity
            label={t('SHIPMENT_FORM_SHIPMENT_QUANTITY_WORKING', { unit: workingUnit })}
            value={formatDerived(workingQuantity)}
          />
        ) : null}
      </Form.Row>
    </Form.Section>
  );
};

// FactorOrDensityField — locked factor within one unit category, editable density across two; `isTracking` only names it.
const FactorOrDensityField = ({
  name,
  from,
  to,
  isDensity,
  rules,
  factor = null,
  disabled = false,
  errorMessage = null,
  isTracking = false,
}) => {
  const t = useTranslations();

  const densityUnits = getDensityUnits(from, to);
  // The tracking pair says so on its label until its endpoint exists.
  const label = isDensity
    ? t(isTracking ? 'SHIPMENT_FORM_TRACKING_DENSITY' : 'SHIPMENT_FORM_DENSITY', {
        weightUnit: densityUnits?.weightUnit ?? '',
        volumeUnit: densityUnits?.volumeUnit ?? '',
      })
    : t(
        isTracking ? 'SHIPMENT_FORM_TRACKING_CONVERSION_FACTOR' : 'SHIPMENT_FORM_CONVERSION_FACTOR',
        {
          toUnit: to,
          fromUnit: from,
        },
      );

  if (!isDensity) {
    return (
      <TextInput
        label={label}
        value={factor == null ? '' : String(factor)}
        onChange={NOOP}
        disabled
      />
    );
  }

  return (
    <Field name={name} rules={rules ?? shipmentValidation.density}>
      {({ id, fieldName, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={fieldName ?? name}
          label={label}
          placeholder={t('SHIPMENT_FORM_DENSITY_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          // Our own copy — the server's conversion errors are not written for users.
          status={errorMessage ? InputStatuses.error : status}
          message={errorMessage ?? message}
          disabled={disabled}
        />
      )}
    </Field>
  );
};

// Read-only mirror of a calculated quantity; blank (never 0) until it can be derived.
const DerivedQuantity = ({ label, value }) => (
  <TextInput label={label} value={value} onChange={NOOP} disabled />
);

// The server accepts the material's working and declaration units here.
const QuantityUnitField = ({ declarationUnit, workingUnit }) => {
  const t = useTranslations();

  // Each entry is badged with which kind of unit it is.
  const unitOptions = useMemo(() => {
    const kinds = [
      [workingUnit, t('UNIT_WORKING')],
      [declarationUnit, t('UNIT_DECLARATION')],
    ];
    const seen = new Set();
    return kinds
      .filter(([unit]) => unit && !seen.has(unit) && seen.add(unit))
      .map(([unit, badgeText]) => ({ value: unit, label: unit, badgeText }));
  }, [workingUnit, declarationUnit, t]);

  return <ShipmentQuantityUnitField options={unitOptions} />;
};

// Disabled inputs never fire it, but TextInput expects the prop.
const NOOP = () => {};

/**
 * Props:
 *   linkedContracts  the contracts already linked to this shipment — nothing renders without one
 *   removed          ids taken off in this session; Save is what actually unlinks them
 *   onUnlink         (contractDbId) => void
 */
const LinkedContracts = ({ linkedContracts, removed, onUnlink }) => {
  const t = useTranslations();

  const items = useMemo(
    () =>
      (linkedContracts ?? EMPTY_LIST)
        .filter((link) => !removed.includes(link.contractDbId))
        .map((link) => ({
          id: link.id,
          label: link.contractId,
          value: link.contractDbId,
        })),
    [linkedContracts, removed],
  );

  return (
    <LinkedEntityField label={t('FIELD_LINKED_CONTRACTS')} items={items} onRemove={onUnlink} />
  );
};
